import { FC, useContext, useMemo, useRef, useState } from "react"

import { CloseOutlined, LeftOutlined } from "@ant-design/icons"
import { Button, Card, Descriptions, Flex, Skeleton, Space, Tabs, TabsProps, Tag } from "antd"
import { useNavigate, useParams } from "react-router-dom"

import classes from "@/_styles/cardById.module.scss"
import variables from "@/_styles/variables.module.scss"
import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { SimpleModal, SimpleModalRef } from "@/components/_Shared/SimpleModal"
import { Table } from "@/components/_Shared/Table"
import { BulkOrderSelectTransportForm, BulkOrderSelectTransportFormProps } from "@/components/BulkOrder/BulkOrderSelectTransportForm/BulkOrderSelectTransportForm"
import { Resource, Scope } from "@/constants/permission"
import { CustomerContext } from "@/context/CustomerContext"

import { shippingSheduleColumns } from "./ShippingSheduleColums"
import { transportColumns } from "./TransportColumns"

import { useBulkOrderByIdQuery } from "@/hook/BulkOrders/useBulkOrderQuery"
import { useShippingScheduleMutation } from "@/hook/ShippingSchedules/useShippingScheduleMutation"
import { usePermission } from "@/hook/usePermission"

import { IShippingSchedule, BulkOrderStatus } from "@/types/IBulkOrder"
import { ITableColumn } from "@/types/ui"

import { paths } from "@/router/paths"

import { formatDate, formatDateTime } from "@/utils/formatDate"

const { Item } = Descriptions

const BulkOrderById: FC = () => {
    const { hasPermission } = usePermission()
    const userContext = useContext(CustomerContext)
    const { id } = useParams<{ id?: string }>()
    const navigate = useNavigate()
    const orderComponent = useRef<BulkOrderSelectTransportFormProps>(null)
    const simpleModalComponent = useRef<SimpleModalRef>(null)

    const [activeKey, setActiveKey] = useState<string>("1")
    const [isAdmin, setIsAdmin] = useState<boolean>(hasPermission(Resource.BulkOrder, Scope.AllActions))


    const { data, isLoading, refetch } = useBulkOrderByIdQuery(Number(id))
    const { cancelShippingScheduleMutation } = useShippingScheduleMutation()

    const acceptDate = useMemo(()=>{
        if(data !== undefined){
            
            var dateTime = formatDateTime(data.response.acceptDate.toString(), "DD.MM.yyyy HH:mm:ss")
            if(dateTime === "-"){
                return formatDateTime(data.response.createdOn.toString(), "DD.MM.yyyy HH:mm:ss")
            }
            return dateTime
        }
    }, [data?.response.acceptDate])

    const onBackHandler = () => {
        navigate(paths.bulkOrders)
    }

    const onCloseShippingSchedule = (id: number) => {
        simpleModalComponent.current?.open({
          title: "Вы точно хотите удалить период?",
          text: "Объем топлива в периоде будет удален без возможности восстановления.",
          ok: {
            text: "Да",
            action: async () => {
              await cancelShippingScheduleMutation.mutateAsync(id)
              await refetch()
              simpleModalComponent.current?.close()
            }
          },
          cancel: {
            text: "Нет",
            action: () => {
                alert("Нет")
                simpleModalComponent.current?.close()
            }
          }
        })
    }


    const isBulkOrderInWork = Number(data?.response.status) === BulkOrderStatus.Accept

    const additionalColumns: ITableColumn<IShippingSchedule>[] = [
        {
            title: "Действие",
            key: "action",
            dataIndex: ["action"],
            isVisible: true,
            order: 5,
            render: ( _, shippingSchedule ) => (
                <Flex gap="20px">
                    <Button
                    disabled={!isBulkOrderInWork || shippingSchedule.isTransportSelect}
                    type="primary"
                    onClick={() => {
                        orderComponent.current?.open(shippingSchedule, data?.response, userContext.customer, refetch)
                        }}>
                            Внести ТС
                    </Button>
                    <ColorifiedButton
                        disabled={!isBulkOrderInWork || shippingSchedule.isTransportSelect}
                        themecolor="orange"       
                        type={"primary"}
                        onClick={() => shippingSchedule.id ? onCloseShippingSchedule(shippingSchedule?.id) : undefined}>
                            Списать
                    </ColorifiedButton>
                </Flex>
            )
        }
    ]

    const title = (
        <div className={classes["header"]}>
            <Flex align="center" gap="20px">
              <ColorifiedButton
                type="primary"
                shape="circle"
                size="small"
                icon={<LeftOutlined />}
                onClick={onBackHandler}
              />
              <span>
                Заказ №{data?.response.id} от {" "}
                {formatDateTime(data?.response.createdOn ?? "-", "DD.MM.yyyy")}{" "}
              </span>
            </Flex>
        </div>
    )

      const tabsItems: TabsProps["items"] = [
        {
            key: "1",
            label: "Топливо",
            children:(
                <>
                    <Table
                        dataSource={data?.response.fuels.flatMap(fuel => fuel.shippingSchedules || [])}
                        columns={shippingSheduleColumns.concat(additionalColumns)}
                        rowKey="id"
                        isLoading={isLoading}
                        permission={{
                            resource: Resource.BulkOrder
                        }}
                        isSelectable={false}
                        isPagination={false}
                    />
                </>
            )
        },
        {
            key: "2",
            label: "Транспорт",
            children: (
                <>
                    <Table
                        dataSource={data?.response.transports}
                        columns={transportColumns}
                        rowKey="id"
                        isLoading={isLoading}
                        permission={{
                            resource: Resource.BulkOrder
                        }}
                        isSelectable={false}
                        isPagination={false}
                        onRowClick={(e) => navigate(paths.orderById(e.orderId)) }
                    />
                </>
            )
        }
      ]
    

    return(
        <Skeleton loading={isLoading}>
            <SimpleModal ref={simpleModalComponent} />
            <BulkOrderSelectTransportForm ref={orderComponent} />
            {data && (
                <div>
                    <Card>
                        <Space direction="vertical">
                            <Descriptions
                            title={title}
                            layout="vertical"
                            column={4}
                            className={classes["descriptions"]}
                            labelStyle={{ 
                              fontWeight: 600, 
                              color: variables.tableSubTitleColor, 
                              marginBottom: "-30px"
                            }}
                            contentStyle={{
                              paddingBottom: "15px"
                            }}>
                                <Item label="Дата создания">
                                    {acceptDate}
                                </Item>
                                <Item label="Объем заказа (в тоннах)">
                                    {data.response.totalFuelQuantity}
                                </Item>
                                <Item label="Остаток (в тоннах)">
                                    {data.response.totalRemainder}
                                </Item>
                                <Item label="Способ доставки">
                                    {data.response.deliveryTypeText}
                                </Item>
                                <Item label="Нефтебаза">
                                    {data.response.depotName}
                                </Item>
                                <Item label="Период отгрузки">
                                    {formatDate(data.response.startDate)} - {formatDate(data.response.endDate)}
                                </Item>
                                <Item label="Статус">
                                    {data.response.statusText}
                                </Item>
                                <Item label="Сумма заказа">
                                    {data.response.amount}
                                </Item>
                                {isAdmin && (
                                    <Item label="Клиент">
                                        {data.response.customerName}
                                    </Item>
                                )}
                            </Descriptions>
                        </Space>
                    </Card>
                    <Card className={classes["card"]} style={{ marginTop: "20px" }}>
                        <Space direction="vertical" className={classes["wrapper"]}>
                            <Tabs
                            defaultActiveKey="1"
                            items={tabsItems}
                            onChange={(e) => {
                                setActiveKey(e)
                            }}
                            />
                            </Space>
                    </Card>
                </div>
            )}
        </Skeleton>
    )
}

export default BulkOrderById